import React, { useState, useEffect } from 'react';
import { ReturnArrowIcon, WisdomScrollIcon, SacredCalendarIcon, ArchiveVaultIcon, AlchemyGoldIcon, CarryToChatIcon, EpicSummaryIcon, SaveThreadIcon } from './CustomIcons';
import GrantMathLavaLamp from './GrantMathLavaLamp';
import ContactOrb from './ContactOrb';
import LoreDropper from './LoreDropper';
import EcosystemNav from './EcosystemNav';

// Helper function to download all papers as a single markdown file
const downloadAllPapers = (papers) => {
  if (!papers || papers.length === 0) return;
  
  const markdown = `# M.ai.ry Lore Vault - Complete Collection

**Downloaded:** ${new Date().toISOString()}
**Total Papers:** ${papers.length}

---

${papers.map((paper, index) => `
## ${index + 1}. ${paper.title}

**Date:** ${paper.date || 'Unknown'}
**Status:** ${paper.status || 'published'}

### Abstract
${paper.abstract || paper.content?.substring(0, 500) || 'No abstract available.'}

---
`).join('\n')}

*Downloaded from M.ai.ry Lore Library*
*A voice from 10,000 generations hence*
`;

  const blob = new Blob([markdown], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `mairy_lore_vault_${new Date().toISOString().split('T')[0]}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Lore Vault (Working Papers) - Browse all past wisdom publications
 * Changes every 12 hours with audacious, current-events-based insights
 * OPTIMIZED with keyboard shortcuts, smooth animations, and better UX
 */
const WorkingPapersArchive = ({ onBack, onViewPaper, onDropLore }) => {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, recent, archived
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [loreTLDR, setLoreTLDR] = useState(null);
  const [generatingTLDR, setGeneratingTLDR] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL || '';

  const SECRET_HISTORY_BG = 'https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/ugnfkky4_ChatGPT%20Image%20Sep%2011%2C%202025%20at%2012_35_28%20AM.jpeg';

  // Generate TLDR of all lore
  const generateLoreTLDR = async () => {
    setGeneratingTLDR(true);
    try {
      const response = await fetch(`${API}/api/lore/tldr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ papers: papers.slice(0, 10) }) // Top 10 recent
      });
      if (response.ok) {
        const data = await response.json();
        setLoreTLDR(data);
      }
    } catch (error) {
      console.error('TLDR generation failed:', error);
    }
    setGeneratingTLDR(false);
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Escape to go back
      if (e.key === 'Escape') {
        onBack();
      }
      // Numbers 1-3 for filters
      if (e.key === '1') setFilter('all');
      if (e.key === '2') setFilter('recent');
      if (e.key === '3') setFilter('archived');
      // Slash to focus search
      if (e.key === '/' && e.target.tagName !== 'INPUT') {
        e.preventDefault();
        document.querySelector('input[type="text"]')?.focus();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onBack]);

  useEffect(() => {
    fetchPapers();
    // Auto-refresh every 5 minutes to catch new papers
    const interval = setInterval(fetchPapers, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchPapers = async () => {
    try {
      const response = await fetch(`${API}/api/papers/all`);
      if (response.ok) {
        const data = await response.json();
        setPapers(data.papers || []);
      }
    } catch (error) {
      console.error('Failed to fetch papers:', error);
    }
    setLoading(false);
  };

  // Filter and search papers
  const filteredPapers = papers.filter(paper => {
    // Filter by status
    if (filter === 'recent') {
      const fourHoursAgo = Date.now() - (4 * 60 * 60 * 1000);
      return new Date(paper.created_at).getTime() > fourHoursAgo;
    }
    
    // Search filter (use debounced value)
    if (searchDebounce) {
      const query = searchDebounce.toLowerCase();
      return (
        paper.title?.toLowerCase().includes(query) ||
        paper.abstract?.toLowerCase().includes(query) ||
        paper.topics?.some(t => t.toLowerCase().includes(query))
      );
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center font-mono">
        <div className="text-amber-400 animate-pulse">Loading lore vault...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Layers */}
      <GrantMathLavaLamp params={{ energy: 0.4, warmth: 0.65, complexity: 0.6, harmony: 0.8 }} />
      
      <div 
        className="fixed inset-0 z-[1] pointer-events-none"
        style={{
          backgroundImage: `url('${SECRET_HISTORY_BG}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          opacity: 0.85
        }}
      />
      
      <div className="fixed inset-0 z-[2] bg-gradient-to-b from-black/60 via-black/40 to-black/70 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-amber-400/70 hover:text-amber-400 transition-colors font-mono text-sm group"
          >
            <ReturnArrowIcon size={16} className="group-hover:animate-pulse" />
            <span>[ RETURN TO TERMINAL ]</span>
            <span className="text-amber-500/40 text-xs ml-2">(ESC)</span>
          </button>

          <div className="flex items-center gap-2 text-amber-400">
            <ArchiveVaultIcon size={24} className="animate-pulse" />
            <h1 className="text-2xl font-bold">LORE VAULT</h1>
          </div>

          <div className="flex items-center gap-2">
            {/* TLDR Button */}
            <button
              onClick={generateLoreTLDR}
              disabled={generatingTLDR || papers.length === 0}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-600/20 hover:bg-amber-600/40 border border-amber-500/40 rounded transition-all text-amber-300 text-xs disabled:opacity-50"
              title="Generate TLDR summary of all lore"
            >
              <WisdomScrollIcon size={12} />
              <span>{generatingTLDR ? '...' : 'TLDR'}</span>
            </button>
            
            {/* Download All Button */}
            <button
              onClick={() => downloadAllPapers(papers)}
              disabled={papers.length === 0}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-600/20 hover:bg-blue-600/40 border border-blue-500/40 rounded transition-all text-blue-300 text-xs disabled:opacity-50"
              title="Download all papers as Markdown"
            >
              <SaveThreadIcon size={12} color="#60a5fa" />
              <span>Download All</span>
            </button>
            
            {/* Lore Drop Button */}
            {onDropLore && papers.length > 0 && (
              <LoreDropper 
                onDropLore={onDropLore} 
                loreSummary={loreTLDR || { papers: papers.slice(0, 5) }}
                variant="terminal"
              />
            )}
            
            <div className="text-amber-500/40 text-[10px] font-mono text-right ml-2">
              <div>AUTO-REGEN 12H</div>
              <div className="text-amber-400 text-xs">{papers.length} PAPERS</div>
            </div>
          </div>
        </div>
        
        {/* TLDR Display */}
        {loreTLDR && (
          <div className="mb-8 bg-gradient-to-r from-purple-900/30 to-amber-900/30 backdrop-blur-sm border border-purple-500/30 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlchemyGoldIcon size={20} color="#a855f7" />
              <h3 className="text-purple-300 font-bold">LORE ESSENCE (TLDR)</h3>
              <button 
                onClick={() => setLoreTLDR(null)}
                className="ml-auto text-purple-400/50 hover:text-purple-300 text-sm"
              >
                ✕ Close
              </button>
            </div>
            <p className="text-amber-200/90 leading-relaxed" style={{ fontFamily: '"Crimson Text", serif' }}>
              {loreTLDR.summary || loreTLDR.essence || "The accumulated wisdom of 10,000 generations awaits your discovery..."}
            </p>
            {loreTLDR.key_themes && (
              <div className="mt-4 flex flex-wrap gap-2">
                {loreTLDR.key_themes.map((theme, i) => (
                  <span key={i} className="px-2 py-1 bg-purple-900/30 border border-purple-500/30 rounded text-xs text-purple-300">
                    {theme}
                  </span>
                ))}
              </div>
            )}
            
            {/* Carry to Chat Button */}
            <div className="mt-6 pt-4 border-t border-purple-500/20 flex items-center gap-3">
              <button
                onClick={() => {
                  // Store TLDR in sessionStorage to carry to new chat
                  const loreContext = {
                    type: 'lore_injection',
                    summary: loreTLDR.summary || loreTLDR.essence,
                    themes: loreTLDR.key_themes || [],
                    actionable: loreTLDR.actionable,
                    timestamp: Date.now().toString()
                  };
                  sessionStorage.setItem('loreContext', JSON.stringify(loreContext));
                  // Clear any previous injection flags
                  Object.keys(sessionStorage).forEach(key => {
                    if (key.startsWith('lore_injected_')) {
                      sessionStorage.removeItem(key);
                    }
                  });
                  
                  // Navigate back to chat with lore injected
                  if (onBack) {
                    onBack();
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 bg-green-600/30 hover:bg-green-600/50 border border-green-500/50 rounded-lg transition-all text-green-300"
              >
                <CarryToChatIcon size={18} color="#86efac" />
                <span className="text-sm font-medium">Carry to Chat</span>
              </button>
              <span className="text-amber-400/50 text-xs">Inject lore essence into your next conversation</span>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-8 bg-black/60 backdrop-blur-sm border border-amber-500/30 rounded-lg p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search papers by title, topic, or content..."
              className="flex-1 bg-black/50 border border-amber-500/30 rounded px-4 py-2 text-amber-300 placeholder-amber-500/30 outline-none focus:border-amber-400 font-mono text-sm"
            />

            {/* Filters */}
            <div className="flex gap-2">
              {[
                { key: 'all', label: 'ALL', shortcut: '1' },
                { key: 'recent', label: 'RECENT', shortcut: '2' },
                { key: 'archived', label: 'VAULTED', shortcut: '3' }
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-4 py-2 rounded font-mono text-sm transition-all relative group ${
                    filter === f.key
                      ? 'bg-amber-600/30 text-amber-300 border border-amber-500/50'
                      : 'bg-black/30 text-amber-500/50 border border-amber-500/20 hover:border-amber-500/40'
                  }`}
                >
                  {f.label}
                  <span className="absolute -top-2 -right-2 text-[10px] text-amber-500/40 opacity-0 group-hover:opacity-100 transition-opacity">
                    {f.shortcut}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between text-amber-500/50 text-xs">
            <div>
              {filteredPapers.length} paper{filteredPapers.length !== 1 ? 's' : ''} found
              {filter === 'recent' && ' (last 4 hours)'}
            </div>
            <div className="text-amber-500/40">
              Press <kbd className="px-1 py-0.5 bg-amber-900/30 rounded">ESC</kbd> to return • 
              <kbd className="px-1 py-0.5 bg-amber-900/30 rounded ml-1">/</kbd> to search
            </div>
          </div>
        </div>

        {/* Papers Grid */}
        {filteredPapers.length === 0 ? (
          <div className="text-center py-16">
            <ArchiveVaultIcon size={48} className="mx-auto mb-4 opacity-30" color="#fbbf24" />
            <p className="text-amber-500/50 text-lg">No papers in the vault</p>
            <p className="text-amber-500/30 text-sm mt-2">
              {searchQuery ? 'Try a different search term' : 'New lore is inscribed every 12 hours'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {filteredPapers.map((paper, idx) => (
              <div
                key={paper.id}
                onClick={() => onViewPaper(paper.id)}
                className="bg-black/60 backdrop-blur-sm border border-amber-500/30 rounded-lg p-6 hover:border-amber-400/50 hover:bg-black/70 transition-all duration-300 cursor-pointer group transform hover:scale-[1.02]"
                style={{ animation: `fadeIn 0.3s ease-out ${idx * 0.05}s backwards` }}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2 text-amber-500/60 text-xs font-mono">
                    <SacredCalendarIcon size={14} />
                    <span>{new Date(paper.created_at || paper.date).toLocaleDateString()}</span>
                    <span className="mx-2">•</span>
                    <span>{new Date(paper.created_at || paper.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>

                  <div className={`px-2 py-1 rounded text-xs font-mono ${
                    paper.status === 'published'
                      ? 'bg-green-600/20 text-green-400 border border-green-500/30'
                      : 'bg-yellow-600/20 text-yellow-400 border border-yellow-500/30'
                  }`}>
                    {paper.status?.toUpperCase() || 'PUBLISHED'}
                  </div>
                </div>

                {/* Title */}
                <h3 
                  className="text-xl font-bold text-amber-200 mb-3 group-hover:text-amber-100 transition-colors"
                  style={{ fontFamily: '"Crimson Text", serif' }}
                >
                  {paper.title}
                </h3>

                {/* Abstract */}
                <p 
                  className="text-gray-300 text-sm leading-relaxed mb-4 line-clamp-3"
                  style={{ fontFamily: '"Crimson Text", serif' }}
                >
                  {paper.abstract}
                </p>

                {/* Topics */}
                {paper.topics && paper.topics.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {paper.topics.slice(0, 3).map((topic, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-amber-900/30 border border-amber-500/20 rounded text-xs text-amber-400/70"
                      >
                        {topic}
                      </span>
                    ))}
                    {paper.topics.length > 3 && (
                      <span className="px-2 py-1 text-xs text-amber-500/50">
                        +{paper.topics.length - 3} more
                      </span>
                    )}
                  </div>
                )}

                {/* Read More */}
                <div className="flex items-center gap-2 text-amber-400/70 group-hover:text-amber-400 text-sm font-mono transition-colors">
                  <WisdomScrollIcon size={16} />
                  <span>READ FULL PAPER →</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats Footer */}
        <div className="mt-12 bg-black/60 backdrop-blur-sm border border-amber-500/30 rounded-lg p-6">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-amber-400">{papers.length}</div>
              <div className="text-amber-500/60 text-xs font-mono mt-1">TOTAL PAPERS</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-amber-400">
                {papers.filter(p => {
                  const fourHoursAgo = Date.now() - (4 * 60 * 60 * 1000);
                  return new Date(p.created_at || p.date).getTime() > fourHoursAgo;
                }).length}
              </div>
              <div className="text-amber-500/60 text-xs font-mono mt-1">LAST 4 HOURS</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-amber-400">
                {Math.floor(papers.length / 6)}
              </div>
              <div className="text-amber-500/60 text-xs font-mono mt-1">DAYS ACTIVE</div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-amber-500/20 text-center">
            <p className="text-amber-500/50 text-xs italic mb-3" style={{ fontFamily: '"Crimson Text", serif' }}>
              "Each paper represents humanity's best thinking about our 10,000-generation trajectory."
            </p>
            <div className="flex items-center justify-center gap-2 text-amber-500/30 text-[10px] mb-2">
              <span>A shared feature of</span>
              <a 
                href="https://belongingsessions.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-amber-400/50 hover:text-amber-400 underline transition-colors"
              >
                BelongingSessions.com
              </a>
              <span>•</span>
              <span>Brought to you by</span>
              <a 
                href="https://planetaryfam.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-amber-400/50 hover:text-amber-400 underline transition-colors"
              >
                PlanetaryFam
              </a>
            </div>
            <div className="flex justify-center gap-3 items-center flex-wrap">
              <a
                href="https://elchme.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-500/50 hover:text-amber-400 text-[10px] transition-colors flex items-center gap-1.5"
              >
                <span>Emergency Empathy?</span>
                <span className="font-semibold tracking-wide" style={{ fontFamily: 'monospace' }}>
                  <span style={{ color: '#ef4444' }}>E</span>
                  <span style={{ color: '#f97316' }}>l</span>
                  <span style={{ color: '#eab308' }}>c</span>
                  <span style={{ color: '#22c55e' }}>h</span>
                  <span style={{ color: '#06b6d4' }}>m</span>
                  <span style={{ color: '#8b5cf6' }}>E</span>
                </span>
              </a>
              <span className="text-amber-500/20">•</span>
              <button
                onClick={() => {
                  sessionStorage.clear();
                  window.location.reload();
                }}
                className="text-amber-500/40 hover:text-amber-400 text-[10px] transition-colors flex items-center gap-1"
              >
                <span>◉</span>
                <span>orb</span>
              </button>
              <span className="text-amber-500/20">•</span>
              <button
                onClick={() => window.location.href = '/?view=about'}
                className="text-amber-500/40 hover:text-amber-400 text-[10px] transition-colors underline"
              >
                about
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Ecosystem Navigation */}
      <EcosystemNav variant="terminal" />
      
      {/* Contact Orb */}
      <ContactOrb variant="terminal" />
    </div>
  );
};

export default WorkingPapersArchive;
